import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, Newspaper, Megaphone, Images, BarChart3, Trophy, Loader2, Radio, ListOrdered, Mail, Upload, Gavel, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Section, DeleteBtn } from "@/components/admin/common";
import LiveTab from "@/components/admin/LiveTab";
import SeasonTab from "@/components/admin/SeasonTab";
import FansTab from "@/components/admin/FansTab";
import AuctionTab from "@/components/admin/AuctionTab";
import RegistrationsTab from "@/components/admin/RegistrationsTab";

const inputCls = "bg-white";

export default function Admin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ news: [], announcements: [], gallery: [], polls: [], winners: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [news, announcements, gallery, polls, winners] = await Promise.all([
        api.get("/news"), api.get("/announcements"), api.get("/gallery"), api.get("/polls"), api.get("/winners"),
      ]);
      setData({ news: news.data, announcements: announcements.data, gallery: gallery.data, polls: polls.data, winners: winners.data });
    } catch {
      toast.error("Failed to load content");
    }
  };

  useEffect(() => { load(); }, []);

  const post = async (path, body, formReset) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success("Published");
      formReset();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (path) => {
    try {
      await api.delete(path);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    navigate("/admin/login");
  };

  const submitNews = (e) => {
    e.preventDefault();
    const f = e.target;
    post("/news", {
      title: f.title.value, body: f.body.value, tag: f.tag.value || "Update",
      image_url: f.image_url.value || null, featured: f.featured.checked,
    }, () => f.reset());
  };

  const submitAnnouncement = (e) => {
    e.preventDefault();
    const f = e.target;
    post("/announcements", { text: f.text.value }, () => f.reset());
  };

  const submitGallery = async (e) => {
    e.preventDefault();
    const f = e.target;
    let imageUrl = f.image_url.value.trim();
    const file = f.photo.files[0];
    if (file) {
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd);
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      } catch (err) {
        setBusy(false);
        toast.error(err.response?.data?.detail || "Upload failed");
        return;
      }
      setBusy(false);
    }
    if (!imageUrl) {
      toast.error("Upload a photo or paste an image URL");
      return;
    }
    post("/gallery", { title: f.title.value, image_url: imageUrl, category: f.category.value || "Moments" }, () => f.reset());
  };

  const submitPoll = (e) => {
    e.preventDefault();
    const f = e.target;
    const options = f.options.value.split("\n").map((s) => s.trim()).filter(Boolean);
    post("/polls", { question: f.question.value, options }, () => f.reset());
  };

  const submitWinner = (e) => {
    e.preventDefault();
    const f = e.target;
    post("/winners", {
      season: Number(f.season.value), year: Number(f.year.value), champion: f.champion.value,
      runner_up: f.runner_up.value, player_of_series: f.pos.value, venue: f.venue.value,
    }, () => f.reset());
  };

  return (
    <main className="pt-24 pb-16 bg-league-mist min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src="/asl-logo.png" alt="All Star League crest" className="h-14 w-auto" data-testid="admin-crest" />
            <div>
              <h1 className="font-display text-3xl sm:text-4xl text-league-ink tracking-tight">LEAGUE CONTROL</h1>
              <p className="text-sm text-muted-foreground mt-1">Publish news, announcements, photos, polls and winners — then run matchday from Live, Season 6 and Fans.</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="admin-logout-button" className="gap-2">
            <LogOut size={15} /> Logout
          </Button>
        </div>

        <Tabs defaultValue="news" data-testid="admin-tabs">
          <TabsList className="mb-6 flex flex-wrap h-auto gap-1 bg-white border border-black/10 p-1">
            <TabsTrigger value="news" data-testid="admin-tab-news" className="gap-2"><Newspaper size={14} /> News</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements" className="gap-2"><Megaphone size={14} /> Announcements</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery" className="gap-2"><Images size={14} /> Gallery</TabsTrigger>
            <TabsTrigger value="polls" data-testid="admin-tab-polls" className="gap-2"><BarChart3 size={14} /> Polls</TabsTrigger>
            <TabsTrigger value="winners" data-testid="admin-tab-winners" className="gap-2"><Trophy size={14} /> Winners</TabsTrigger>
            <TabsTrigger value="live" data-testid="admin-tab-live" className="gap-2"><Radio size={14} /> Live</TabsTrigger>
            <TabsTrigger value="season" data-testid="admin-tab-season" className="gap-2"><ListOrdered size={14} /> Season 6</TabsTrigger>
            <TabsTrigger value="fans" data-testid="admin-tab-fans" className="gap-2"><Mail size={14} /> Fans & Leads</TabsTrigger>
            <TabsTrigger value="auction" data-testid="admin-tab-auction" className="gap-2"><Gavel size={14} /> Auction</TabsTrigger>
            <TabsTrigger value="players" data-testid="admin-tab-players" className="gap-2"><Users size={14} /> Players</TabsTrigger>
          </TabsList>

          <TabsContent value="news">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Post a story">
                <form onSubmit={submitNews} className="space-y-4" data-testid="admin-news-form">
                  <div><Label htmlFor="news-title">Title</Label><Input id="news-title" name="title" required className={inputCls} data-testid="admin-news-title" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="news-tag">Tag</Label><Input id="news-tag" name="tag" placeholder="Announcement" className={inputCls} data-testid="admin-news-tag" /></div>
                    <div><Label htmlFor="news-image">Image URL</Label><Input id="news-image" name="image_url" placeholder="https://…" className={inputCls} data-testid="admin-news-image" /></div>
                  </div>
                  <div><Label htmlFor="news-body">Story</Label><Textarea id="news-body" name="body" required rows={4} className={inputCls} data-testid="admin-news-body" /></div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-league-ink">
                    <Checkbox name="featured" data-testid="admin-news-featured" /> Pin as featured story
                  </label>
                  <Button type="submit" disabled={busy} data-testid="admin-news-submit" className="gap-2">{busy && <Loader2 size={14} className="animate-spin" />}<Plus size={15} /> Publish</Button>
                </form>
              </Section>
              <Section title={`Stories (${data.news.length})`}>
                <ul className="divide-y divide-black/5">
                  {data.news.map((n) => (
                    <li key={n.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-news-item-${n.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-league-ink leading-snug">{n.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{n.tag}{n.featured ? " • Featured" : ""}</p>
                      </div>
                      <DeleteBtn testid={`admin-news-delete-${n.id}`} onClick={() => remove(`/news/${n.id}`)} />
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="announcements">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="New ticker announcement">
                <form onSubmit={submitAnnouncement} className="space-y-4" data-testid="admin-announcement-form">
                  <div><Label htmlFor="ann-text">Announcement text</Label><Textarea id="ann-text" name="text" required rows={3} placeholder="Shows in the blue marquee strip on the homepage" className={inputCls} data-testid="admin-announcement-text" /></div>
                  <Button type="submit" disabled={busy} data-testid="admin-announcement-submit" className="gap-2"><Plus size={15} /> Publish</Button>
                </form>
              </Section>
              <Section title={`Live announcements (${data.announcements.length})`}>
                <ul className="divide-y divide-black/5">
                  {data.announcements.map((a) => (
                    <li key={a.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-announcement-item-${a.id}`}>
                      <p className="text-sm text-league-ink">{a.text}</p>
                      <DeleteBtn testid={`admin-announcement-delete-${a.id}`} onClick={() => remove(`/announcements/${a.id}`)} />
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="gallery">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Add a photo">
                <form onSubmit={submitGallery} className="space-y-4" data-testid="admin-gallery-form">
                  <div><Label htmlFor="gal-title">Caption</Label><Input id="gal-title" name="title" required className={inputCls} data-testid="admin-gallery-title" /></div>
                  <div>
                    <Label htmlFor="gal-photo">Upload photo — straight from your phone</Label>
                    <input
                      id="gal-photo"
                      name="photo"
                      type="file"
                      accept="image/*"
                      data-testid="admin-gallery-file"
                      className="mt-1.5 w-full text-sm text-league-ink file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:bg-league-blue file:text-white file:font-semibold file:text-xs file:uppercase file:tracking-wider hover:file:bg-league-deep file:cursor-pointer"
                    />
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP or GIF, max 10 MB — or paste a link below instead.</p>
                  </div>
                  <div><Label htmlFor="gal-image">Image URL (optional if uploading)</Label><Input id="gal-image" name="image_url" placeholder="https://…" className={inputCls} data-testid="admin-gallery-image" /></div>
                  <div><Label htmlFor="gal-cat">Category</Label><Input id="gal-cat" name="category" placeholder="Matches / Fans / Champions" className={inputCls} data-testid="admin-gallery-category" /></div>
                  <Button type="submit" disabled={busy} data-testid="admin-gallery-submit" className="gap-2">{busy && <Loader2 size={14} className="animate-spin" />}<Upload size={15} /> Add to gallery</Button>
                </form>
              </Section>
              <Section title={`Photos (${data.gallery.length})`}>
                <ul className="divide-y divide-black/5">
                  {data.gallery.map((g) => (
                    <li key={g.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-gallery-item-${g.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={g.image_url} alt="" className="w-12 h-12 rounded-md object-cover shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-league-ink truncate">{g.title}</p>
                          <p className="text-xs text-muted-foreground">{g.category}</p>
                        </div>
                      </div>
                      <DeleteBtn testid={`admin-gallery-delete-${g.id}`} onClick={() => remove(`/gallery/${g.id}`)} />
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="polls">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Create a poll">
                <form onSubmit={submitPoll} className="space-y-4" data-testid="admin-poll-form">
                  <div><Label htmlFor="poll-q">Question</Label><Input id="poll-q" name="question" required className={inputCls} data-testid="admin-poll-question" /></div>
                  <div><Label htmlFor="poll-opts">Options — one per line (min 2)</Label><Textarea id="poll-opts" name="options" required rows={4} className={inputCls} data-testid="admin-poll-options" /></div>
                  <Button type="submit" disabled={busy} data-testid="admin-poll-submit" className="gap-2"><Plus size={15} /> Launch poll</Button>
                </form>
              </Section>
              <Section title={`Active polls (${data.polls.length})`}>
                <ul className="divide-y divide-black/5">
                  {data.polls.map((p) => (
                    <li key={p.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-poll-item-${p.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-league-ink">{p.question}</p>
                        <p className="text-xs text-muted-foreground mt-1">{p.options.length} options • {p.options.reduce((s, o) => s + o.votes, 0)} votes</p>
                      </div>
                      <DeleteBtn testid={`admin-poll-delete-${p.id}`} onClick={() => remove(`/polls/${p.id}`)} />
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="winners">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Record a season result">
                <form onSubmit={submitWinner} className="space-y-4" data-testid="admin-winner-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="win-season">Season no.</Label><Input id="win-season" name="season" type="number" min="1" required className={inputCls} data-testid="admin-winner-season" /></div>
                    <div><Label htmlFor="win-year">Year</Label><Input id="win-year" name="year" type="number" min="2020" required className={inputCls} data-testid="admin-winner-year" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="win-champ">Champion</Label><Input id="win-champ" name="champion" required className={inputCls} data-testid="admin-winner-champion" /></div>
                    <div><Label htmlFor="win-runner">Runners-up</Label><Input id="win-runner" name="runner_up" required className={inputCls} data-testid="admin-winner-runnerup" /></div>
                  </div>
                  <div><Label htmlFor="win-pos">Player of the Series</Label><Input id="win-pos" name="pos" required className={inputCls} data-testid="admin-winner-pos" /></div>
                  <div><Label htmlFor="win-venue">Venue</Label><Input id="win-venue" name="venue" required className={inputCls} data-testid="admin-winner-venue" /></div>
                  <Button type="submit" disabled={busy} data-testid="admin-winner-submit" className="gap-2"><Plus size={15} /> Add to hall of fame</Button>
                </form>
              </Section>
              <Section title={`Seasons (${data.winners.length})`}>
                <ul className="divide-y divide-black/5">
                  {data.winners.map((w) => (
                    <li key={w.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-winner-item-${w.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-league-ink">S{w.season}.0 ({w.year}) — {w.champion}</p>
                        <p className="text-xs text-muted-foreground mt-1">vs {w.runner_up} • PoS {w.player_of_series}</p>
                      </div>
                      <DeleteBtn testid={`admin-winner-delete-${w.id}`} onClick={() => remove(`/winners/${w.id}`)} />
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <LiveTab />
          <SeasonTab />
          <FansTab />
          <AuctionTab />
          <RegistrationsTab />
        </Tabs>
      </div>
    </main>
  );
}
